/**
 * Browser side of lead attribution (see shared/attribution.ts for the rules).
 *
 * The landing visit is always kept in memory for the current tab, so a form sent
 * in the same session carries it. It is written to localStorage — and so survives
 * other days — only while the Semantiko CMP reports marketing consent; an explicit
 * refusal removes anything stored earlier.
 */
import {
  attributionForSubmit,
  emptyAttribution,
  recordVisit,
  type AttributionFields,
  type AttributionState,
} from "@shared/attribution";

const STORAGE_KEY = "nu_attribution";

let memoryState: AttributionState = {};
let captured = false;

function marketingConsent(): boolean | null {
  try {
    const consent = window.SemantikoCMP?.getConsent();
    if (!consent) return null;
    return consent.marketing === true;
  } catch {
    return null;
  }
}

function readStored(): AttributionState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/** Older stored data wins for "first"; the newer of the two wins for "latest". */
function merge(stored: AttributionState, current: AttributionState): AttributionState {
  const latest =
    (current.latest?.seenAt ?? 0) >= (stored.latest?.seenAt ?? 0)
      ? current.latest ?? stored.latest
      : stored.latest;
  return { latest, first: stored.first ?? current.first };
}

function syncWithStorage() {
  const consent = marketingConsent();
  try {
    if (consent === true) {
      memoryState = merge(readStored(), memoryState);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryState));
    } else if (consent === false) {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* Storage can be unavailable (private mode); memory state still works. */
  }
}

/** Call once on page load, before any client-side navigation changes the URL. */
export function captureAttribution() {
  if (typeof window === "undefined" || captured) return;
  captured = true;
  const consent = marketingConsent();
  const base = consent === true ? readStored() : {};
  memoryState = recordVisit(base, window.location.href, new Date());
  syncWithStorage();
  // The CMP may load (or consent be given) after landing; retry when leaving.
  window.addEventListener("pagehide", syncWithStorage);
}

/** The 15 CRM fields for a submission; always every key, empty when unknown. */
export function getAttributionFields(): AttributionFields {
  if (typeof window === "undefined") return emptyAttribution();
  syncWithStorage();
  return attributionForSubmit(memoryState, Date.now());
}
